import { useMemo, useState } from 'react';

type UseSortState<T> = {
  sortField?: keyof T;
  sortDirection: 'asc' | 'desc';
};

export type SortData<T> = {
  sortField: keyof T;
  sortDirection: 'asc' | 'desc';
  setSortField: (sortField: keyof T) => void;
};

export function useSort<T>(list: T[] | undefined, defaultSortField?: keyof T, defaultSortDirection?: 'asc' | 'desc') {
  const [state, setState] = useState<UseSortState<T>>({
    sortDirection: defaultSortDirection ?? 'asc',
    sortField: defaultSortField
  });

  const sortedList = useMemo(() => {
    const { sortField, sortDirection } = state;
    if (!sortField || !list) {
      return list;
    }
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    const sorter = (a: T, b: T) => {
      let aVal = a[sortField] as any;
      let bVal = b[sortField] as any;

      // handle sorting when null values are present
      if (aVal == null && bVal == null) {
        return 0;
      }
      if (aVal == null) {
        return 1;
      }
      if (bVal == null) {
        return -1;
      }

      if (typeof aVal.toLowerCase === 'function') {
        aVal = aVal.toLowerCase();
      }
      if (typeof bVal.toLowerCase === 'function') {
        bVal = bVal.toLowerCase();
      }

      if (aVal > bVal) {
        return multiplier;
      }
      if (aVal < bVal) {
        return -multiplier;
      }
      return 0;
    };
    return list.sort(sorter);
  }, [list, state]);

  return {
    sortedList,
    sortData: {
      sortField: state.sortField,
      sortDirection: state.sortDirection,
      setSortField: (sortField: keyof T) => {
        if (state.sortField === sortField) {
          setState({ ...state, sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc' });
        } else {
          setState({ sortField, sortDirection: 'asc' });
        }
      },
    } as SortData<T>,
  };
}
